import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import SEOHead from './SEO/SEOHead';
import SchemaMarkup from './SEO/SchemaMarkup';
import { Box, Container, Grid, Typography, Paper, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SectionTitle from './shared/SectionTitle';
// ***** Import social icons when needed
// import { Facebook, Twitter, Instagram, LinkedIn } from '@mui/icons-material';

// ***** Adjust card heights and spacing here
const CONTACT_SIZING = {
    CARD_HEIGHT: '200px',
    CARD_MIN_WIDTH: '300px',
    SPACING: {
        SECTION: '48px',
        ELEMENT: '24px'
    }
} as const;

const ContactCard = styled(Paper)(({ theme }) => ({
    height: CONTACT_SIZING.CARD_HEIGHT,
    minWidth: CONTACT_SIZING.CARD_MIN_WIDTH,
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.divider}`,
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: theme.shadows[4]
    }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    '& .MuiSvgIcon-root': {
        fontSize: '2.5rem',
        color: theme.palette.primary.main
    }
}));

// ***** Social media component - uncomment when ready
/*
const SocialLinks = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    marginTop: theme.spacing(4),
    '& .MuiSvgIcon-root': {
        fontSize: '2rem',
        cursor: 'pointer',
        transition: 'color 0.3s ease',
        '&:hover': {
            color: theme.palette.primary.main
        }
    }
}));
*/

const ContactPage: React.FC = () => {
    return (
        <Box sx={{ py: CONTACT_SIZING.SPACING.SECTION }}>
            <SEOHead page="contact" />
            <SchemaMarkup type="contactPage" />
            <Container maxWidth="lg">
                <SectionTitle centered sx={{ mb: 1 }}>
                    Contact Us
                </SectionTitle>
                <Typography variant="subtitle1" align="center" sx={{ mb: 6 }}>
                    We're here to help with all your sailmaking needs
                </Typography>

                <Grid container spacing={4} justifyContent="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <ContactCard>
                            <IconWrapper>
                                <PhoneIcon />
                            </IconWrapper>
                            <Typography variant="h6" gutterBottom>
                                Phone
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                (619) 663-9330
                            </Typography>
                        </ContactCard>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <Link href="mailto:crew@pointsails.com" sx={{ textDecoration: 'none', color: 'inherit' }}>
                            <ContactCard>
                                <IconWrapper>
                                    <EmailIcon />
                                </IconWrapper>
                                <Typography variant="h6" gutterBottom>
                                    Email
                                </Typography>
                                <Link href="mailto:crew@pointsails.com">crew@pointsails.com</Link>
                            </ContactCard>
                        </Link>
                    </Grid>

                    {/* ***** Location card - uncomment when ready
                    <Grid item xs={12} sm={6} md={4}>
                        <ContactCard>
                            <IconWrapper>
                                <LocationOnIcon />
                            </IconWrapper>
                            <Typography variant="h6" gutterBottom>
                                Location
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Your Address Here
                            </Typography>
                        </ContactCard>
                    </Grid>
                    */}
                </Grid>

                {/* ***** Contact form section - uncomment when ready
                <Box sx={{ mt: CONTACT_SIZING.SPACING.SECTION }}>
                    <Typography variant="h5" align="center" gutterBottom>
                        Send Us a Message
                    </Typography>
                    <Paper sx={{ p: 4, mt: 4, maxWidth: '800px', mx: 'auto' }}>
                        Contact form component will go here
                    </Paper>
                </Box>
                */}

                {/* ***** Social media section - uncomment when ready
                <SocialLinks>
                    <Facebook />
                    <Twitter />
                    <Instagram />
                    <LinkedIn />
                </SocialLinks>
                */}

                {/* ***** FAQ section - uncomment when ready
                <Box sx={{ mt: CONTACT_SIZING.SPACING.SECTION }}>
                    <Typography variant="h5" align="center" gutterBottom>
                        Frequently Asked Questions
                    </Typography>
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        FAQ components will go here
                    </Grid>
                </Box>
                */}
            </Container>
        </Box>
    );
};

export default ContactPage;
